/**
 * Redux slice for the Audit Queue page.
 *
 * Follows the same pattern as auditSlice.ts (createAsyncThunk + extraReducers).
 *
 * Thunks call auditModuleService against the real /audit_queue and
 * /audit_config backend endpoints. The pages render under the VLM Audit
 * Workflow tab (/vlmAudit).
 */
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { LOADING_STATE } from "src/types/constants";
import AuditModuleService from "src/services/auditModuleService";
import { saveWorkspaceResult } from "src/store/auditWorkspaceSlice";
import { QueueDateRange, defaultQueueRange } from "src/components/pages/auditModule/shared/helpers";

// ----------------------------  Types  ----------------------------
export interface AuditQueueUseCase {
  id: string;
  name: string;
  inter_rater_count: number;
}

export interface AuditQueueTask {
  id: string;
  use_case_id: string;
  status: string;
  assigned_to: string | null;
  priority: number;
  created_at: string;
  // auditors assigned to this record (Review Sampling tiers: 1-3)
  reviewer_count?: number;
  // sample review progress: Unreviewed | PartiallyReviewed | FullyReviewed
  review_status?: string;
}

// One answer of a review: a field decision or a checklist answer, on the record or
// on one object of an object presence record.
export interface AuditQueueAnswer {
  // the field decision (correct / incorrect) or the checklist button picked
  decision: string;
  // the field label or checklist question (the key when missing)
  label?: string;
  kind?: "field" | "checklist";
  // the object answered on; null / missing for the record itself
  object_name?: string | null;
}

export interface AuditQueueResult {
  id: string;
  task_id: string;
  auditor_id: string;
  status: string;
  // keyed so the same row has the same key across reviewers
  decisions: Record<string, AuditQueueAnswer>;
}

export interface AuditQueueUser {
  login: string;
  name: string;
  role: string;
  isElevated: boolean;
}

export interface AuditQueueState {
  useCases: AuditQueueUseCase[];
  tasks: AuditQueueTask[];
  results: AuditQueueResult[];
  currentUser: AuditQueueUser;
  getUseCasesLoading: LOADING_STATE;
  getTasksLoading: LOADING_STATE;
  getResultsLoading: LOADING_STATE;
  // set once the first load finishes; a later refresh then keeps showing the old list
  tasksLoaded: boolean;
  resultsLoaded: boolean;
  // the queue's date range; `tasks` and the progress counts cover only this range
  dateRange: QueueDateRange;
  // the backend capped the range's tasks (only the newest are in `tasks`)
  truncated: boolean;
  // record-ID search results at any date; null until the latest search returns
  searchTasks: AuditQueueTask[] | null;
  searchTerm: string;
  // state of the latest search request
  searchLoading: LOADING_STATE;
  // latest request ids, so a slow older response never overwrites a newer one
  tasksRequestId: string | null;
  searchRequestId: string | null;
  resultsRequestId: string | null;
}

const initialState: AuditQueueState = {
  useCases: [],
  tasks: [],
  results: [],
  currentUser: { login: "", name: "", role: "", isElevated: false },
  getUseCasesLoading: "idle",
  getTasksLoading: "idle",
  getResultsLoading: "idle",
  tasksLoaded: false,
  resultsLoaded: false,
  dateRange: defaultQueueRange(),
  truncated: false,
  searchTasks: null,
  searchTerm: "",
  searchLoading: "idle",
  tasksRequestId: null,
  searchRequestId: null,
  resultsRequestId: null,
};

// ----------------------------  Async Thunks  ----------------------------

export const getAuditQueueUseCases = createAsyncThunk("auditQueue/getUseCases", async () => {
  // Use cases are derived from the active configs stored in DB.
  const response = await AuditModuleService.getUseCaseConfigs();
  return response.configs.map((c) => ({
    id: c.id,
    name: c.name,
    inter_rater_count: c.inter_rater_count,
  }));
});

export const getAuditQueueTasks = createAsyncThunk(
  "auditQueue/getTasks",
  async (range: QueueDateRange) => {
    const response = await AuditModuleService.getTasks({
      start_date: range.start,
      end_date: range.end,
    });
    return { tasks: response.tasks, truncated: !!response.truncated };
  },
);

// Record-ID search across every date (the backend ignores the range for a search).
export const searchAuditQueueTasks = createAsyncThunk(
  "auditQueue/searchTasks",
  async (term: string) => {
    const response = await AuditModuleService.getTasks({ search: term });
    return response.tasks;
  },
);

export const getAuditQueueResults = createAsyncThunk(
  "auditQueue/getResults",
  async (args: { taskIds: string[]; currentAlias?: string }) => {
    const response = await AuditModuleService.getResults(args.taskIds);
    return response.results;
  },
);

// ----------------------------  Slice  ----------------------------

const { reducer, actions } = createSlice({
  name: "auditQueueSlice",
  initialState,
  reducers: {
    setCurrentUser: (state, { payload }: { payload: AuditQueueUser }) => {
      state.currentUser = payload;
    },
    setQueueDateRange: (state, { payload }: { payload: QueueDateRange }) => {
      state.dateRange = payload;
    },
    clearQueueSearch: (state) => {
      state.searchTasks = null;
      state.searchTerm = "";
      state.searchRequestId = null;
      state.searchLoading = "idle";
    },
  },
  extraReducers: (builder) => {
    // Use cases
    builder.addCase(getAuditQueueUseCases.pending, (state) => {
      state.getUseCasesLoading = "pending";
    });
    builder.addCase(getAuditQueueUseCases.fulfilled, (state, { payload }) => {
      state.useCases = payload;
      state.getUseCasesLoading = "fulfilled";
    });
    builder.addCase(getAuditQueueUseCases.rejected, (state) => {
      state.getUseCasesLoading = "rejected";
    });

    // Tasks
    builder.addCase(getAuditQueueTasks.pending, (state, { meta }) => {
      state.getTasksLoading = "pending";
      state.tasksRequestId = meta.requestId;
    });
    builder.addCase(getAuditQueueTasks.fulfilled, (state, { payload, meta }) => {
      if (meta.requestId !== state.tasksRequestId) return;
      state.tasks = payload.tasks;
      state.truncated = payload.truncated;
      state.getTasksLoading = "fulfilled";
      state.tasksLoaded = true;
    });
    builder.addCase(getAuditQueueTasks.rejected, (state, { meta }) => {
      if (meta.requestId !== state.tasksRequestId) return;
      state.getTasksLoading = "rejected";
    });

    // Record-ID search
    builder.addCase(searchAuditQueueTasks.pending, (state, { meta }) => {
      state.searchRequestId = meta.requestId;
      state.searchLoading = "pending";
    });
    builder.addCase(searchAuditQueueTasks.fulfilled, (state, { payload, meta }) => {
      if (meta.requestId !== state.searchRequestId) return;
      state.searchTasks = payload;
      state.searchTerm = meta.arg;
      state.searchLoading = "fulfilled";
    });
    builder.addCase(searchAuditQueueTasks.rejected, (state, { meta }) => {
      if (meta.requestId !== state.searchRequestId) return;
      state.searchTasks = null;
      state.searchTerm = "";
      state.searchLoading = "rejected";
    });

    // Results
    builder.addCase(getAuditQueueResults.pending, (state, { meta }) => {
      state.resultsRequestId = meta.requestId;
      state.getResultsLoading = "pending";
    });
    builder.addCase(getAuditQueueResults.fulfilled, (state, { payload, meta }) => {
      if (meta.requestId !== state.resultsRequestId) return;
      state.results = payload;
      state.getResultsLoading = "fulfilled";
      state.resultsLoaded = true;
    });
    builder.addCase(getAuditQueueResults.rejected, (state, { meta }) => {
      if (meta.requestId !== state.resultsRequestId) return;
      state.getResultsLoading = "rejected";
    });

    // A save in the workspace moves the task at once (submit -> Completed, draft ->
    // in progress), before the queue's own refresh comes back.
    builder.addCase(saveWorkspaceResult.fulfilled, (state, { payload }) => {
      const moved = (t: AuditQueueTask): AuditQueueTask => {
        if (t.id !== payload.task_id) return t;
        if (payload.status === "submitted") return { ...t, status: "completed" };
        return t.status === "completed" || t.status === "expired" ? t : { ...t, status: "draft" };
      };
      state.tasks = state.tasks.map(moved);
      if (state.searchTasks) state.searchTasks = state.searchTasks.map(moved);
    });
  },
});

export const { setCurrentUser, setQueueDateRange, clearQueueSearch } = actions;

export default reducer;
